import UserStore from "./UserStore";
import SearchSessionStore from "./SearchSessionStore";
import EChainStore from "./EChainStore";
import AppStatusStore from "./AppStatusStore";
import TutorialStore from "./TutorialStore";
import HistoryStore from "./HistoryStore";
import SourceReferencesStore from "./SourceReferencesStore";
import { entityDiscoveryStore } from "./EntityDiscoveryStore";
import { WorkspaceStore } from "./WorkspaceStore";
import EntitlementsStore from "./EntitlementsStore";
import SkuCatalogStore from "./SkuCatalogStore";
import UsersStore from "./UsersStore";
import UsageStore from "./UsageStore";
import { CustomerFilesStore } from "./CustomerFilesStore";
import { subscribeToProgress, setAuthGetter } from "../lib/uploadClient";
import { friendlyError } from "../lib/friendlyError";
import { setAuthGetter as setCustomerFilesAuthGetter } from "../lib/customerFilesApi";
import { DEV, getConfig } from "../lib/configMgr";

const RESULTS_BATCH_SIZE = getConfig().resultsBatchSize || 500;
const appStatusStore = new AppStatusStore();
const userStore = new UserStore(appStatusStore);
const searchSessionStore = new SearchSessionStore(
  userStore,
  appStatusStore,
  RESULTS_BATCH_SIZE,
);
const eChainStore = new EChainStore(userStore, appStatusStore, searchSessionStore);
const tutorialStore = new TutorialStore({ userStore, appStatusStore, searchSessionStore, eChainStore });
const historyStore = new HistoryStore(userStore);
searchSessionStore.historyStore = historyStore;
const sourceReferencesStore = new SourceReferencesStore();
const workspaceStore = new WorkspaceStore(userStore);

export const rootStore = {
  userStore,
  searchSessionStore,
  eChainStore,
  appStatusStore,
  tutorialStore,
  historyStore,
  sourceReferencesStore,
  entityDiscoveryStore,
  workspaceStore,
};
// Entitlements store needs access to userStore (for authData + userName) — wire
// it after rootStore is shaped so the constructor can read back into it.
rootStore.entitlementsStore = new EntitlementsStore(rootStore);
// Same pattern for the global SKU catalog store.
rootStore.skuCatalogStore = new SkuCatalogStore(rootStore);
// Same pattern for the read-only tenant roster store.
rootStore.usersStore = new UsersStore(rootStore);
// Same pattern for the read-only usage-series store.
rootStore.usageStore = new UsageStore(rootStore);

// customerFilesApi (page-side REST) AND the SW (via uploadClient) both use
// raw fetch and bypass request.js, so they need the Bearer header threaded
// in from userStore.jwtToken. The plex_ui_key cookie is path=/api and so
// invisible from /v2/* pages — don't rely on it.
const buildAuth = () => (userStore.jwtToken ? `Bearer ${userStore.jwtToken}` : null);
setAuthGetter(buildAuth);
setCustomerFilesAuthGetter(buildAuth);

// CustomerFilesStore singleton — instantiated here so the BroadcastChannel
// subscription is registered exactly once (not inside a per-render effect).
const customerFilesStore = new CustomerFilesStore();
rootStore.customerFilesStore = customerFilesStore;

subscribeToProgress((msg) => {
  if (msg.type === "upload_progress") {
    customerFilesStore.updateUploadProgress(msg.file_id, msg.progress);
  } else if (msg.type === "upload_complete") {
    customerFilesStore.updateUploadProgress(msg.file_id, 1);
    // Upsert the finished file into the current project's list so it appears
    // without a manual reload (updateUploadProgress no-ops for files not yet
    // in the store, which a just-uploaded file isn't).
    customerFilesStore.onUploadComplete(msg.file_id);
  } else if (msg.type === "upload_failed") {
    // Surface a human-readable error in the Data drawer (was previously
    // swallowed to the console for pre-init failures, so a user dragging a file
    // during e.g. an expired session saw nothing happen).
    const named = msg.name ? `Couldn't upload “${msg.name}”.` : "Upload failed. Please try again.";
    customerFilesStore.setUploadError(friendlyError(msg.reason, named));
    if (msg.file_id != null && customerFilesStore.projectId) {
      // Also reflect the failure on the row that already exists.
      customerFilesStore.applyChange(customerFilesStore.projectId, {
        file_id: msg.file_id,
        status: "failed",
        action: "status_changed",
        last_error: msg.reason,
      });
    }
  } else if (msg.type === "upload_paused") {
    // Synthesize a file entry so the DataDrawer can surface the Resume
    // affordance. The projectId guard in applyChange ensures this only
    // updates the UI when the user is on the affected project — if they're
    // on a different project, the paused state will surface when loadFiles
    // is called for that project via listInflight.
    customerFilesStore.applyChange(msg.project_id, {
      file_id: msg.file_id,
      name: msg.name,
      status: "paused",
      last_error: msg.reason,
      action: "status_changed",
    });
  }
});

// Expose rootStore for debugging in dev mode
if (DEV) {
  window.__rootStore = rootStore;
}
